import { findRecord } from '@warp-drive/utilities/json-api';
import { warn } from '@ember/debug';

import type { Store } from '@warp-drive/core';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

/**
 * PaperTrail "whodunnit" / audit fields (`created_by`, `updated_by`) only hold
 * a user foreign key when an authenticated user made the change. System, seed,
 * and import actions leave a non-numeric string (e.g. `"demo-user"`) or a JSONB
 * audit object — neither of which resolves to `GET /users/:id`.
 *
 * Returns true only for values that can safely be used as a `users` record id.
 */
export function isResolvableUserId(value: unknown): value is string | number {
  if (typeof value === 'number') return Number.isInteger(value);
  return typeof value === 'string' && /^\d+$/.test(value);
}

/**
 * Resolve an audit/whodunnit value to a display name.
 *
 * - Numeric user id that resolves → the user's full name.
 * - Non-numeric string (e.g. `"demo-user"`) → the raw string, with NO request
 *   issued (a `GET /users/demo-user` would 404 and surface as an unhandled
 *   promise rejection under WarpDrive).
 * - Anything else (a JSONB audit object, null) → `''`.
 * - A numeric id that 404s (e.g. a since-deleted user) → falls back to the raw
 *   value rather than rejecting. Any other failure (5xx, network) is a real
 *   error: it's warned in DEBUG and resolves to `''` rather than being masked
 *   as an id-like display value.
 */
export async function resolveAuditUserName(store: Store, value: unknown): Promise<string> {
  if (!isResolvableUserId(value)) {
    return typeof value === 'string' ? value : '';
  }

  try {
    const { content } = (await store.request(
      findRecord<UserModel>('user', String(value), { include: 'person' })
    )) as { content: { data: UserModel } };
    const person = content.data?.person;
    return person ? `${person.firstName} ${person.lastName}` : '';
  } catch (error) {
    // Only a 404 (since-deleted user) falls back to the raw id; other failures
    // (5xx, connectivity) are real errors we must not silently mask as a name.
    if ((error as { status?: number })?.status === 404) {
      return String(value);
    }
    warn(
      `resolve-audit-user: failed to resolve user "${String(value)}" ` +
        `(status ${(error as { status?: number })?.status ?? 'unknown'})`,
      false,
      { id: 'tio-common.resolve-audit-user.request-failed' }
    );
    return '';
  }
}
